import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip, Legend,
} from "chart.js";
import { Bar, Line, Scatter } from "react-chartjs-2";
import * as api from "../api/client";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip, Legend);

const num = (v, d = 2) => Number(v).toLocaleString("en-IN", { maximumFractionDigits: d });
const fmt = (v) => {
  if (v == null) return "—";
  const a = Math.abs(v);
  if (a >= 1e7) return `${num(v / 1e7, a >= 1e9 ? 0 : 2)} Cr`;
  if (a >= 1e5) return `${num(v / 1e5)} L`;
  if (a >= 1e4) return `${num(v / 1e3, 1)} K`;
  return num(v, 3);
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const period = (p, freq) => (freq === "month" ? `${MONTHS[Number(p.slice(5, 7)) - 1]} ${p.slice(0, 4)}` : p);
const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);
const ago = (s) => (s ? new Date(s * 1000).toLocaleString() : "—");
const css = (name, fallback) => {
  const v = typeof window !== "undefined" && getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};

const TASK = {
  classification: ["Classification", "Predicts which outcome a row will have"],
  regression: ["Regression", "Predicts a number for each row"],
  forecast: ["Forecast", "Projects a measure forward in time"],
};

function headline(m) {
  if (!m.metrics) return null;
  if (m.task === "classification") return { label: "Accuracy", value: pct(m.metrics.accuracy), base: m.baseline && `${pct(m.baseline.accuracy)} by ${m.baseline.label}` };
  if (m.task === "regression") return { label: "R²", value: m.metrics.r2?.toFixed(3), base: m.baseline && `typical error ±${fmt(m.metrics.mae)} vs ±${fmt(m.baseline.mae)} using the ${m.baseline.label}` };
  return { label: "Back-test error", value: pct(m.metrics.wape), base: m.baseline && `${pct(m.baseline.wape)} using the ${m.baseline.label} (lower is better)` };
}

function StatusPill({ m }) {
  if (m.status === "running") return <span className="cx-status running">Training</span>;
  if (m.status === "failed") return <span className="cx-status failed">Failed</span>;
  return <span className="cx-status ok">Ready</span>;
}

export default function ModelsPage() {
  const [models, setModels] = useState(null);
  const [error, setError] = useState("");
  const [wizard, setWizard] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setModels((await api.listMlModels()).models);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const running = (models || []).some((m) => m.status === "running");
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [running, load]);

  return (
    <div className="cx-page">
      <header className="cx-head">
        <div>
          <h1 className="cx-title">Models</h1>
          <p className="cx-sub">Train prediction and forecasting models on your QLens tables, compare algorithms, see what drives the result, and write predictions back for Chat and dashboards. Training runs on this server; data never leaves it.</p>
        </div>
        <button type="button" className="cx-btn primary" onClick={() => setWizard({ kind: "" })}>+ New model</button>
      </header>
      {error && <div className="cx-msgbox bad">{error}</div>}

      {models === null ? (
        <div className="cx-card cx-loading"><Spinner animation="border" size="sm" /></div>
      ) : models.length === 0 ? (
        <div className="ml-starts">
          <StartCard kind="predict" onPick={(k) => setWizard({ kind: k })} />
          <StartCard kind="forecast" onPick={(k) => setWizard({ kind: k })} />
        </div>
      ) : (
        <section className="cx-card">
          <div className="cx-scroll ml-list">
            <table className="cx-table">
              <thead><tr><th>Model</th><th>Type</th><th>Table</th><th>Predicts</th><th>Best algorithm</th><th>Score</th><th>Status</th><th>Created</th></tr></thead>
              <tbody>
                {models.map((m) => {
                  const h = headline(m);
                  return (
                    <tr key={m.id} className="ml-row" onClick={() => navigate(`/models/${m.id}`)}>
                      <td className="cx-strong"><Link to={`/models/${m.id}`}>{m.name}</Link></td>
                      <td><span className={`ml-type ${m.task}`}>{TASK[m.task][0]}</span></td>
                      <td>{m.dataset}</td>
                      <td>{m.kind === "forecast" ? `${m.config.agg === "count" ? "rows" : `${m.config.agg} of ${m.config.value}`} / ${m.config.freq}` : m.target}</td>
                      <td>{m.algorithm || "—"}</td>
                      <td>{h ? <><strong>{h.value}</strong> <span className="cx-muted">{h.label}</span></> : "—"}</td>
                      <td><StatusPill m={m} />{m.status === "running" && <div className="cx-muted">{m.stage}</div>}</td>
                      <td className="cx-muted">{ago(m.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {wizard && <NewModelModal initialKind={wizard.kind} onClose={() => setWizard(null)}
                                onCreated={(m) => navigate(`/models/${m.id}`)} />}
    </div>
  );
}

function StartCard({ kind, onPick }) {
  const predict = kind === "predict";
  return (
    <button type="button" className="ml-start" onClick={() => onPick(kind)}>
      <span className={`ml-start-ic ${kind}`}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d={predict ? "M12 3v3m0 12v3M3 12h3m12 0h3M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" : "M3 17l5-5 4 4 8-9M15 7h5v5"} />
        </svg>
      </span>
      <strong>{predict ? "Predict an outcome" : "Forecast a trend"}</strong>
      <span>{predict
        ? "Learn from past rows to predict a column: will a booking cancel, which customers pay late, what a deal will be worth."
        : "Project sales, collections or volumes forward by day, week or month, with an expected range."}</span>
    </button>
  );
}

function NewModelModal({ initialKind, onClose, onCreated }) {
  const [kind, setKind] = useState(initialKind);
  const [datasets, setDatasets] = useState([]);
  const [f, setF] = useState({ name: "", dataset: "", target: "", task: "", timeColumn: "", valueColumn: "", agg: "sum", freq: "month", horizon: 6 });
  const [prof, setProf] = useState(null);
  const [feats, setFeats] = useState([]);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api.listMlDatasets().then((r) => setDatasets(r.datasets)).catch((e) => setErr(e.message));
  }, []);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const ds = datasets.find((d) => d.key === f.dataset);
  const cols = ds ? ds.columns : [];
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  useEffect(() => {
    if (kind !== "predict" || !f.dataset || !f.target) {
      setProf(null);
      return undefined;
    }
    let live = true;
    setBusy("profile");
    setErr("");
    api.profileMl(f.dataset, f.target)
      .then((p) => {
        if (!live) return;
        setProf(p);
        setFeats(p.features);
        setF((x) => ({ ...x, task: p.task }));
      })
      .catch((e) => live && setErr(e.message))
      .finally(() => live && setBusy(""));
    return () => { live = false; };
  }, [kind, f.dataset, f.target]);

  const targetKind = cols.find((c) => c.name === f.target)?.kind;
  const canTrain = f.name.trim() && f.dataset && (kind === "predict"
    ? f.target && prof && feats.length > 0
    : f.timeColumn && (f.agg === "count" || f.valueColumn));

  async function train(e) {
    e.preventDefault();
    setBusy("train");
    setErr("");
    try {
      const body = kind === "predict"
        ? { name: f.name.trim(), kind, dataset: f.dataset, target: f.target, task: f.task, features: feats }
        : { name: f.name.trim(), kind, dataset: f.dataset, timeColumn: f.timeColumn, valueColumn: f.agg === "count" ? null : f.valueColumn,
            agg: f.agg, freq: f.freq, horizon: Number(f.horizon) };
      onCreated(await api.createMlModel(body));
    } catch (er) {
      setErr(er.message);
      setBusy("");
    }
  }

  const toggle = (c) => setFeats((xs) => (xs.includes(c) ? xs.filter((x) => x !== c) : [...xs, c]));
  const maxH = { day: 90, week: 52, month: 24 }[f.freq];

  return (
    <div className="modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="cx-modal ml-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="New model">
        <div className="cx-modal-h">
          <div>
            <div className="cx-modal-title">New model</div>
            <div className="cx-muted">{kind ? (kind === "predict" ? "Predict an outcome" : "Forecast a trend") : "What do you want to do?"}</div>
          </div>
          <button type="button" className="cx-x-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        {!kind ? (
          <div className="cx-modal-body ml-starts in-modal">
            <StartCard kind="predict" onPick={setKind} />
            <StartCard kind="forecast" onPick={setKind} />
          </div>
        ) : (
          <form onSubmit={train} className="cx-form-wrap">
            <div className="cx-modal-body">
              <div className="cx-grid">
                <label className="cx-field">
                  <span>Model name</span>
                  <input value={f.name} onChange={set("name")} maxLength={60} required
                         placeholder={kind === "predict" ? "e.g. Booking cancellation risk" : "e.g. Monthly sales forecast"} />
                </label>
                <label className="cx-field">
                  <span>Table</span>
                  <select value={f.dataset} onChange={(e) => setF((x) => ({ ...x, dataset: e.target.value, target: "", timeColumn: "", valueColumn: "" }))} required>
                    <option value="">Choose a table…</option>
                    {datasets.map((d) => <option key={d.key} value={d.key}>{d.key}</option>)}
                  </select>
                </label>
                {kind === "predict" && ds && (
                  <>
                    <label className="cx-field">
                      <span>Column to predict</span>
                      <select value={f.target} onChange={set("target")} required>
                        <option value="">Choose a column…</option>
                        {cols.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.kind})</option>)}
                      </select>
                    </label>
                    <label className="cx-field">
                      <span>Prediction type</span>
                      <select value={f.task} onChange={set("task")} disabled={!prof}>
                        <option value="classification">Outcome / category (classification)</option>
                        {targetKind === "number" && <option value="regression">Number (regression)</option>}
                      </select>
                    </label>
                  </>
                )}
                {kind === "forecast" && ds && (
                  <>
                    <label className="cx-field">
                      <span>Date column</span>
                      <select value={f.timeColumn} onChange={set("timeColumn")} required>
                        <option value="">Choose…</option>
                        {cols.filter((c) => c.kind === "date").map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                        {cols.filter((c) => c.kind === "text").length > 0 && (
                          <optgroup label="Text columns holding dates">
                            {cols.filter((c) => c.kind === "text").map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                          </optgroup>
                        )}
                      </select>
                    </label>
                    <label className="cx-field">
                      <span>What to forecast</span>
                      <select value={f.agg} onChange={set("agg")}>
                        <option value="sum">Total of a column</option>
                        <option value="avg">Average of a column</option>
                        <option value="count">Number of rows</option>
                      </select>
                    </label>
                    {f.agg !== "count" && (
                      <label className="cx-field">
                        <span>Column</span>
                        <select value={f.valueColumn} onChange={set("valueColumn")} required>
                          <option value="">Choose a number column…</option>
                          {cols.filter((c) => c.kind === "number").map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                        </select>
                      </label>
                    )}
                    <label className="cx-field">
                      <span>Every</span>
                      <select value={f.freq} onChange={(e) => setF((x) => ({ ...x, freq: e.target.value, horizon: { day: 30, week: 8, month: 6 }[e.target.value] }))}>
                        <option value="day">Day</option><option value="week">Week</option><option value="month">Month</option>
                      </select>
                    </label>
                    <label className="cx-field">
                      <span>Periods ahead</span>
                      <input type="number" min={1} max={maxH} value={f.horizon} onChange={set("horizon")} />
                    </label>
                  </>
                )}
              </div>

              {kind === "predict" && busy === "profile" && <div className="cx-msgbox"><Spinner size="sm" animation="border" /> Checking the columns…</div>}
              {kind === "predict" && prof && (
                <div className="ml-feats">
                  <div className="ml-feats-h">
                    <strong>Columns to learn from</strong>
                    <span className="cx-muted">{feats.length} selected · {prof.targetFilled}% of rows have a value for “{f.target}”</span>
                  </div>
                  <div className="ml-feat-list">
                    {prof.features.map((c) => (
                      <label key={c} className="ml-feat"><input type="checkbox" checked={feats.includes(c)} onChange={() => toggle(c)} /> {c}</label>
                    ))}
                  </div>
                  {prof.excluded.length > 0 && (
                    <>
                      <div className="ml-feats-h sub"><strong>Left out automatically</strong><span className="cx-muted">tick to include anyway</span></div>
                      <div className="ml-feat-list">
                        {prof.excluded.map((e) => (
                          <label key={e.column} className="ml-feat off" title={e.reason}>
                            <input type="checkbox" checked={feats.includes(e.column)} onChange={() => toggle(e.column)} />
                            <span>{e.column}<small>{e.reason}</small></span>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              {err && <div className="cx-msgbox bad">{err}</div>}
            </div>
            <div className="cx-modal-f">
              {!initialKind && <button type="button" className="cx-btn" onClick={() => setKind("")} disabled={!!busy}>Back</button>}
              <button type="submit" className="cx-btn primary" disabled={!canTrain || !!busy}>
                {busy === "train" ? <Spinner size="sm" animation="border" /> : "Train model"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export function ModelDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("eval");

  const load = useCallback(async () => {
    try {
      setM(await api.getMlModel(id));
    } catch (e) {
      setErr(e.message);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const busy = m && (m.status === "running" || m.output.status === "running");
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(load, 1500);
    return () => clearInterval(t);
  }, [busy, load]);

  async function act(fn, confirmText) {
    if (confirmText && !window.confirm(confirmText)) return;
    setErr("");
    try {
      const r = await fn();
      if (r && r.id) setM(r);
      return r;
    } catch (e) {
      setErr(e.message);
      return null;
    }
  }

  if (!m) {
    return <div className="cx-page">{err ? <div className="cx-msgbox bad">{err}</div> : <div className="cx-loading"><Spinner animation="border" size="sm" /></div>}</div>;
  }
  const res = m.results || {};
  const h = headline(m);
  const forecast = m.kind === "forecast";
  const tabs = forecast
    ? [["eval", "Forecast"], ["runs", "Methods compared"], ["use", "Use in QLens"]]
    : [["eval", "Evaluation"], ["explain", "What drives it"], ["runs", "Algorithms compared"], ["use", "Use model"]];

  return (
    <div className="cx-page">
      <Link to="/models" className="ml-back">← Models</Link>
      <header className="cx-head">
        <div>
          <h1 className="cx-title">{m.name} <span className={`ml-type ${m.task}`}>{TASK[m.task][0]}</span></h1>
          <p className="cx-sub">
            {forecast
              ? `${m.config.agg === "count" ? "Number of rows" : `${m.config.agg === "sum" ? "Total" : "Average"} ${m.config.value}`} in ${m.dataset}, per ${m.config.freq} by ${m.target}, ${m.config.horizon} ${m.config.freq}s ahead.`
              : `Predicts “${m.target}” in ${m.dataset} from ${m.features.length} columns.`}
            {" "}Created by {m.createdBy || "—"} · {ago(m.createdAt)}
          </p>
        </div>
        <div className="ml-actions">
          <button type="button" className="cx-btn" disabled={busy} onClick={() => act(() => api.retrainMlModel(m.id))}>Retrain</button>
          <button type="button" className="cx-btn danger" disabled={busy}
                  onClick={async () => (await act(() => api.deleteMlModel(m.id), `Delete “${m.name}”${m.output.table ? ` and its table ${m.output.table}` : ""}?`)) && navigate("/models")}>Delete</button>
        </div>
      </header>
      {err && <div className="cx-msgbox bad">{err}</div>}

      {m.status === "running" && (
        <div className="cx-card ml-progress">
          <Spinner animation="border" />
          <div>
            <strong>Training…</strong>
            <div className="cx-muted">{m.stage || "Starting"}</div>
          </div>
        </div>
      )}
      {m.status === "failed" && <div className="cx-msgbox bad">Training failed: {m.error}</div>}

      {m.status === "ok" && (
        <>
          <div className="ml-stats">
            <Stat label={h.label} value={h.value} sub={h.base && `Baseline: ${h.base}`} primary />
            {forecast ? (
              <>
                <Stat label={`Next ${m.config.freq}`} value={fmt(res.forecast?.[0]?.value)} sub={res.forecast?.[0] && `${fmt(res.forecast[0].low)} – ${fmt(res.forecast[0].high)} (80% range)`} />
                <Stat label="Method" value={res.algorithm} small sub={`Best of ${res.runs?.filter((r) => r.status === "ok").length} methods`} />
                <Stat label="History" value={`${res.metrics.periods} ${m.config.freq}s`} sub={`Back-tested on the last ${res.metrics.testPeriods}`} />
              </>
            ) : (
              <>
                {m.task === "classification"
                  ? <Stat label="F1 score" value={pct(res.metrics.f1)} sub={res.metrics.rocAuc != null ? `ROC AUC ${res.metrics.rocAuc.toFixed(3)}` : "weighted across outcomes"} />
                  : <Stat label="Typical error" value={`±${fmt(res.metrics.mae)}`} sub={`RMSE ${fmt(res.metrics.rmse)}`} />}
                <Stat label="Best algorithm" value={res.algorithm} small sub={`Best of ${res.runs?.filter((r) => r.status === "ok").length} compared`} />
                <Stat label="Rows" value={fmt(res.rows.train + res.rows.test)} sub={`${fmt(res.rows.train)} to learn, ${fmt(res.rows.test)} held out to test`} />
              </>
            )}
          </div>

          {(res.warnings?.length > 0 || res.notes?.length > 0) && (
            <div className="ml-notes">
              {res.warnings?.map((w) => <div key={w} className="cx-beta">{w}</div>)}
              {res.notes?.map((n) => <div key={n} className="cx-muted">{n}</div>)}
            </div>
          )}

          <section className="cx-card ml-body">
            <div className="cx-tabs">
              {tabs.map(([k, l]) => <button key={k} type="button" className={"cx-tab" + (tab === k ? " on" : "")} onClick={() => setTab(k)}>{l}</button>)}
            </div>
            {tab === "eval" && (forecast ? <ForecastView m={m} /> : m.task === "classification" ? <Confusion res={res} /> : <ActualVsPredicted res={res} target={m.target} />)}
            {tab === "explain" && <Importance res={res} />}
            {tab === "runs" && <Runs m={m} />}
            {tab === "use" && <UsePanel m={m} onScore={() => act(() => api.scoreMl(m.id))}
                                         onSchedule={(v) => act(() => api.setMlSchedule(m.id, v))} />}
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub, primary, small }) {
  return (
    <div className={"cx-card ml-stat" + (primary ? " primary" : "")}>
      <div className="ml-stat-l">{label}</div>
      <div className={"ml-stat-v" + (small ? " small" : "")}>{value ?? "—"}</div>
      {sub && <div className="cx-muted">{sub}</div>}
    </div>
  );
}

function Confusion({ res }) {
  const { labels, matrix } = res.confusion || { labels: [], matrix: [] };
  const max = Math.max(1, ...matrix.flat());
  return (
    <div className="ml-two">
      <div>
        <h3 className="ml-h">Held-out rows: actual vs predicted</h3>
        <p className="cx-muted">Each cell counts test rows the model never saw while learning. The diagonal is correct.</p>
        <div className="ml-cm" style={{ gridTemplateColumns: `auto repeat(${labels.length}, minmax(64px, 1fr))` }}>
          <div className="ml-cm-corner">Actual ↓ / Predicted →</div>
          {labels.map((l) => <div key={l} className="ml-cm-h">{l}</div>)}
          {matrix.map((row, i) => (
            <div key={labels[i]} className="ml-cm-row">
              <div className="ml-cm-h left">{labels[i]}</div>
              {row.map((v, j) => (
                <div key={j} className={"ml-cm-c" + (i === j ? " diag" : "")} style={{ "--a": (v / max).toFixed(3) }}>{fmt(v)}</div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="ml-h">Outcomes in the data</h3>
        <table className="cx-table ml-mini">
          <tbody>{(res.classes || []).map((c) => <tr key={c.label}><td>{c.label}</td><td className="num">{fmt(c.rows)}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

function ActualVsPredicted({ res, target }) {
  const pts = (res.scatter || []).map(([x, y]) => ({ x, y }));
  const lo = Math.min(...pts.map((p) => Math.min(p.x, p.y)));
  const hi = Math.max(...pts.map((p) => Math.max(p.x, p.y)));
  const data = {
    datasets: [
      { label: "Held-out rows", data: pts, backgroundColor: css("--chart-1", "#4f46e5") + "99", pointRadius: 3 },
      { label: "Perfect prediction", type: "line", data: [{ x: lo, y: lo }, { x: hi, y: hi }], borderColor: "#94a3b8", borderDash: [6, 4], pointRadius: 0 },
    ],
  };
  const opts = {
    maintainAspectRatio: false,
    scales: { x: { title: { display: true, text: `Actual ${target}` }, ticks: { callback: fmt } },
              y: { title: { display: true, text: `Predicted ${target}` }, ticks: { callback: fmt } } },
    plugins: { tooltip: { callbacks: { label: (c) => `actual ${fmt(c.parsed.x)}, predicted ${fmt(c.parsed.y)}` } } },
  };
  return (
    <div>
      <h3 className="ml-h">Actual vs predicted on held-out rows</h3>
      <p className="cx-muted">Points close to the dashed line are accurate predictions. Rows shown were not used for learning.</p>
      <div className="ml-chart"><Scatter data={data} options={opts} /></div>
    </div>
  );
}

function Importance({ res }) {
  const items = (res.importance || []).slice(0, 15);
  const data = {
    labels: items.map((i) => i.feature),
    datasets: [{ data: items.map((i) => +(i.share * 100).toFixed(1)), backgroundColor: css("--chart-1", "#4f46e5"), borderRadius: 4 }],
  };
  const opts = {
    indexAxis: "y", maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `${c.parsed.x}% of the model's accuracy` } } },
    scales: { x: { ticks: { callback: (v) => `${v}%` } } },
  };
  return (
    <div>
      <h3 className="ml-h">What drives the predictions</h3>
      <p className="cx-muted">How much accuracy drops when each column is shuffled (permutation importance), as a share of the total.</p>
      {items.every((i) => i.share === 0)
        ? <div className="cx-msgbox">No single column matters on its own; the model relies on several overlapping columns.</div>
        : <div className="ml-chart" style={{ height: Math.max(220, items.length * 28) }}><Bar data={data} options={opts} /></div>}
    </div>
  );
}

function Runs({ m }) {
  const res = m.results;
  const forecast = m.kind === "forecast";
  const cols = forecast ? [["wape", "Error %", pct], ["mae", "Avg. error", fmt], ["rmse", "RMSE", fmt]]
    : m.task === "classification" ? [["accuracy", "Accuracy", pct], ["f1", "F1", pct], ["precision", "Precision", pct], ["recall", "Recall", pct], ["rocAuc", "ROC AUC", (v) => (v == null ? "—" : v.toFixed(3))]]
      : [["r2", "R²", (v) => (v == null ? "—" : v.toFixed(3))], ["mae", "Avg. error", fmt], ["rmse", "RMSE", fmt]];
  return (
    <div>
      <p className="cx-muted">{forecast
        ? "Each method forecast the most recent periods using only the history before them; the lowest error wins."
        : "Every algorithm learned from the same rows and was scored on the same held-out rows; the best F1 / R² wins."}</p>
      <div className="cx-scroll">
        <table className="cx-table">
          <thead><tr><th>{forecast ? "Method" : "Algorithm"}</th>{cols.map(([, l]) => <th key={l}>{l}</th>)}{!forecast && <th>Time</th>}<th /></tr></thead>
          <tbody>
            {res.runs.map((r) => (
              <tr key={r.algorithm} className={r.best ? "ml-best" : ""}>
                <td className="cx-strong">{r.algorithm}</td>
                {r.status === "ok" ? cols.map(([k, l, f]) => <td key={l}>{f(r.metrics[k])}</td>) : <td colSpan={cols.length} className="cx-err">{r.error}</td>}
                {!forecast && <td className="cx-muted">{r.seconds}s</td>}
                <td>{r.best && <span className="cx-pill verified">Best</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ForecastView({ m }) {
  const res = m.results;
  const hist = res.history.slice(-48);
  const freq = m.config.freq;
  const labels = [...hist.map((p) => period(p.period, freq)), ...res.forecast.map((p) => period(p.period, freq))];
  const pad = (arr, before) => [...Array(before).fill(null), ...arr];
  const bt = Object.fromEntries(res.backtest.map((b) => [b.period, b.predicted]));
  const c1 = css("--chart-1", "#4f46e5");
  const c2 = css("--chart-2", "#059669");
  const lastHist = hist[hist.length - 1];
  const fcVals = [lastHist.value, ...res.forecast.map((p) => p.value)];
  const data = {
    labels,
    datasets: [
      { label: "Actual", data: hist.map((p) => p.value), borderColor: c1, backgroundColor: c1, pointRadius: 2, tension: 0.2 },
      { label: "Back-test", data: hist.map((p) => bt[p.period] ?? null), borderColor: "#94a3b8", borderDash: [4, 4], pointRadius: 2, spanGaps: true },
      { label: "Range high", data: pad(res.forecast.map((p) => p.high), hist.length), borderWidth: 0, pointRadius: 0, backgroundColor: c2 + "26", fill: "+1" },
      { label: "Range low", data: pad(res.forecast.map((p) => p.low), hist.length), borderWidth: 0, pointRadius: 0 },
      { label: "Forecast", data: pad(fcVals, hist.length - 1), borderColor: c2, backgroundColor: c2, borderDash: [6, 3], pointRadius: 3, tension: 0.2 },
    ],
  };
  const opts = {
    maintainAspectRatio: false, interaction: { mode: "index", intersect: false },
    plugins: { legend: { labels: { filter: (i) => !i.text.startsWith("Range") } },
               tooltip: { callbacks: { label: (c) => (c.parsed.y == null ? null : `${c.dataset.label}: ${fmt(c.parsed.y)}`) } } },
    scales: { y: { ticks: { callback: fmt } }, x: { ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } } },
  };
  return (
    <div>
      <div className="ml-chart tall"><Line data={data} options={opts} /></div>
      <h3 className="ml-h">Forecast</h3>
      <div className="cx-scroll">
        <table className="cx-table">
          <thead><tr><th>Period</th><th>Forecast</th><th>Likely low</th><th>Likely high</th></tr></thead>
          <tbody>{res.forecast.map((p) => <tr key={p.period}><td>{period(p.period, freq)}</td><td className="cx-strong">{fmt(p.value)}</td><td>{fmt(p.low)}</td><td>{fmt(p.high)}</td></tr>)}</tbody>
        </table>
      </div>
      <p className="cx-muted cx-note">The range covers about 80% of outcomes, estimated from the back-test error.</p>
    </div>
  );
}

function UsePanel({ m, onScore, onSchedule }) {
  const forecast = m.kind === "forecast";
  const out = m.output;
  return (
    <div className="ml-use">
      {!forecast && <TryIt m={m} />}
      <div className="cx-card ml-write">
        <h3 className="ml-h">{forecast ? "Write the forecast to QLens" : "Predict every row and write it to QLens"}</h3>
        <p className="cx-muted">{forecast
          ? "Creates a table with the history and the forecast, so you can ask about it in Chat and add it to dashboards."
          : `Scores every row of ${m.dataset} and saves the predictions${m.task === "classification" ? " and confidence" : ""} next to the actual values as a new table, ready for Chat, dashboards and role-based access.`}</p>
        {out.status === "running" && <div className="cx-msgbox"><Spinner size="sm" animation="border" /> Writing…</div>}
        {out.status === "ok" && (
          <div className="cx-msgbox ok">
            {fmt(out.rows)} rows in <code>{out.table}</code> · {ago(out.at)}. <Link to="/">Ask about it in Chat</Link>. Grant access on Settings › Roles.
          </div>
        )}
        {out.status === "failed" && <div className="cx-msgbox bad">{out.error}</div>}
        <div><button type="button" className="cx-btn primary" disabled={out.status === "running"} onClick={onScore}>
          {out.status === "ok" ? "Write again" : forecast ? "Write forecast table" : "Write predictions table"}
        </button></div>
      </div>
      <Freshness m={m} onSchedule={onSchedule} />
    </div>
  );
}

const METRIC = { accuracy: ["Accuracy", pct], r2: ["R²", (v) => (v == null ? "—" : v.toFixed(3))], wape: ["Error", pct] };

function Freshness({ m, onSchedule }) {
  const versions = [...(m.history || [])].reverse();
  return (
    <div className="cx-card ml-write">
      <h3 className="ml-h">Keep it up to date as new data arrives</h3>
      <p className="cx-muted">New rows land in {m.dataset} every day. On schedule, QLens retrains on the latest data, records a new
        version with its score, and rewrites the {m.kind === "forecast" ? "forecast" : "predictions"} table so Chat and
        dashboards always use current results. Retrain also does this on demand.</p>
      <div className="ml-sched">
        <label className="cx-field">
          <span>Retrain automatically</span>
          <select value={m.schedule || "off"} onChange={(e) => onSchedule(e.target.value)}>
            <option value="off">Off</option><option value="daily">Daily</option>
            <option value="weekly">Weekly</option><option value="monthly">Monthly</option>
          </select>
        </label>
        {m.schedule && m.schedule !== "off" && <span className="cx-muted">Next run {ago(m.nextRunAt)}</span>}
      </div>
      {versions.length > 0 && (
        <div className="cx-scroll">
          <table className="cx-table">
            <thead><tr><th>Version</th><th>Trained</th><th>Trigger</th><th>Algorithm</th><th>Rows</th><th>Score</th></tr></thead>
            <tbody>
              {versions.map((v, i) => {
                const [label, f] = METRIC[v.metric] || ["Score", fmt];
                return (
                  <tr key={v.at}>
                    <td className="cx-strong">v{versions.length - i}{i === 0 && <span className="cx-pill verified ml-live">Live</span>}</td>
                    <td className="cx-muted">{ago(v.at)}</td><td>{v.trigger}</td><td>{v.algorithm}</td>
                    <td>{fmt(v.rows)}</td><td>{label} {f(v.value)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TryIt({ m }) {
  const feats = m.features;
  const [vals, setVals] = useState({});
  const [out, setOut] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const shown = useMemo(() => {
    const top = (m.results.importance || []).map((i) => i.feature).filter((f) => feats.includes(f));
    return [...top, ...feats.filter((f) => !top.includes(f))];
  }, [m, feats]);

  async function run(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      setOut((await api.predictMl(m.id, [vals])).predictions[0]);
    } catch (er) {
      setErr(er.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="cx-card ml-try" onSubmit={run}>
      <h3 className="ml-h">Try a prediction</h3>
      <p className="cx-muted">Fill in what you know; blanks are treated as unknown. Most influential columns first.</p>
      <div className="ml-try-grid">
        {shown.map((f) => (
          <label key={f} className="cx-field">
            <span>{f}</span>
            <input value={vals[f] || ""} onChange={(e) => setVals((v) => ({ ...v, [f]: e.target.value }))} />
          </label>
        ))}
      </div>
      <div className="ml-try-f">
        <button type="submit" className="cx-btn primary" disabled={busy}>{busy ? <Spinner size="sm" animation="border" /> : "Predict"}</button>
        {out && (
          <div className="ml-answer">
            Predicted {m.target}: <strong>{m.task === "regression" ? fmt(out.value) : out.value}</strong>
            {out.confidence != null && <span className="cx-muted"> · {pct(out.confidence)} confident</span>}
          </div>
        )}
      </div>
      {err && <div className="cx-msgbox bad">{err}</div>}
    </form>
  );
}
